import TasksBoard from "./Kanban/TasksBoard";
import { Helmet } from "react-helmet";
import SecondHeader from "../Header/SecondHeader";
import trans from "../../utils";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { popupcontext } from "../../context/Popupscontext";
import TasksForm from "./Forms/TasksForm";
import TasksHeader from "./TasksHeader";
import TasksList from "./List/TasksList";
import dayjs from "dayjs";
import TasksBoardGroupByPOI from "./Kanban/TasksBoardGroupByPOI";
import TasksBoardGroupByFieldStaff from "./Kanban/TasksBoardGroupByFieldStaff";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import TaskBulkUpload from "./BulkUpload/TaskBulkUpload";
import { Alert, Button, Space } from "antd";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useGetSpecificRequestQuery } from "../../Redux/service/Requests/Request";
import {
	KANBAN_COLUMN_DEFINITIONS,
	KANBAN_COLUMNS_STORAGE_KEY,
	loadVisibleKanbanColumnIds,
} from "./Kanban/kanbanColumns";
import {
	TASK_LIST_COLUMN_DEFINITIONS,
	TASK_LIST_COLUMNS_STORAGE_KEY,
	loadVisibleTaskListColumnKeys,
} from "./List/taskListColumns";

const Tasks = () => {
	let { handleShow } = useContext(popupcontext);
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedServiceRequestId, setSelectedServiceRequestId] = useState(
		searchParams.get("serviceRequestId") || null
	);
	const [viewMode, setViewMode] = useState("Kanban");
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const [selectedRange, setSelectedRange] = useState([
		dayjs().startOf("day"), // from today
		dayjs().endOf("day"), // to today
	]);
	const [selectedPOIs, setSelectedPOIs] = useState([]);
	const [selectedStaff, setSelectedStaff] = useState([]);
	const [refresh, setRefresh] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [visibleColumnIds, setVisibleColumnIds] = useState(
		loadVisibleKanbanColumnIds
	);
	const [visibleListColumnIds, setVisibleListColumnIds] = useState(
		loadVisibleTaskListColumnKeys
	);

	const handleColumnVisibilityChange = (nextIds) => {
		setVisibleColumnIds(nextIds);
		localStorage.setItem(KANBAN_COLUMNS_STORAGE_KEY, JSON.stringify(nextIds));
	};

	const handleListColumnVisibilityChange = (nextIds) => {
		setVisibleListColumnIds(nextIds);
		localStorage.setItem(
			TASK_LIST_COLUMNS_STORAGE_KEY,
			JSON.stringify(nextIds)
		);
	};

	const fromDate = selectedDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
	const toDate = selectedDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
	const fromDateList = selectedRange?.[0]?.format("YYYY-MM-DDTHH:mm:ss");
	const toDateList = selectedRange?.[1]?.format("YYYY-MM-DDTHH:mm:ss");

	const { view, create } = usePermission("FIELD_TASKS");
	const [showBulkUpload, setShowBulkUpload] = useState(false);

	const { data: requestDetails } = useGetSpecificRequestQuery(
		selectedServiceRequestId,
		{ skip: !selectedServiceRequestId }
	);

	const clearServiceRequestFilter = () => {
		setSelectedServiceRequestId(null);
		setSelectedRange([
			dayjs().startOf("day"),
			dayjs().endOf("day"),
		]);
		const nextParams = new URLSearchParams(searchParams);
		nextParams.delete("serviceRequestId");
		setSearchParams(nextParams, { replace: true });
	};

	return (
		<>
			<Helmet>
				<title>Tasks Board</title>
				<meta name="description" content="Users" />
			</Helmet>
			<SecondHeader
				add={create ? trans("Add new task") : null}
				onClick={() => handleShow("addForm")}
				secButton={create ? trans("Bulk Add Tasks") : null}
				secClick={() => setShowBulkUpload(true)}
			/>
			{selectedServiceRequestId && (
				<div className="bg-white px-4 pt-3">
					<Alert
						message={
							<Space>
								<InfoCircleOutlined />
								<strong>{trans("Filtered by Service Request")}</strong>
							</Space>
						}
						description={
							<Space wrap>
								<span>
									{`${trans("Code")}: ${
										requestDetails?.data?.data?.code || selectedServiceRequestId
									}`}
								</span>
								{requestDetails?.data?.data?.description && (
									<span>
										{`${trans("Description")}: ${requestDetails.data.data.description}`}
									</span>
								)}
							</Space>
						}
						type="info"
						showIcon={false}
						action={
							<Button
								size="small"
								onClick={clearServiceRequestFilter}
								icon={<CloseOutlined />}
							>
								{trans("Clear filter")}
							</Button>
						}
					/>
				</div>
			)}
			<TasksHeader
				viewMode={viewMode}
				setViewMode={setViewMode}
				selectedDate={selectedDate}
				setSelectedDate={setSelectedDate}
				selectedRange={selectedRange}
				setSelectedRange={setSelectedRange}
				selectedPOIs={selectedPOIs}
				setSelectedPOIs={setSelectedPOIs}
				selectedStaff={selectedStaff}
				setSelectedStaff={setSelectedStaff}
				columns={KANBAN_COLUMN_DEFINITIONS}
				visibleColumnIds={visibleColumnIds}
				onColumnVisibilityChange={handleColumnVisibilityChange}
				listColumns={TASK_LIST_COLUMN_DEFINITIONS}
				visibleListColumnIds={visibleListColumnIds}
				onListColumnVisibilityChange={handleListColumnVisibilityChange}
			/>
			{viewMode === "Kanban" && (
				<TasksBoard
					fromDate={fromDateList}
					toDate={toDateList}
					setRefresh={setRefresh}
					refresh={refresh}
					setRefreshing={setRefreshing}
					viewMode={viewMode}
					serviceRequestId={selectedServiceRequestId}
					visibleColumnIds={visibleColumnIds}
				/>
			)}
			{viewMode === "Kanban - POI" && (
				<TasksBoardGroupByPOI
					fromDate={fromDateList}
					toDate={toDateList}
					setRefresh={setRefresh}
					refresh={refresh}
					setRefreshing={setRefreshing}
					viewMode={viewMode}
					selectedPOIs={selectedPOIs}
					showBy="poi"
					serviceRequestId={selectedServiceRequestId}
					visibleColumnIds={visibleColumnIds}
				/>
			)}
			{viewMode === "Kanban - Staff" && (
				<TasksBoardGroupByFieldStaff
					fromDate={fromDateList}
					toDate={toDateList}
					setRefresh={setRefresh}
					refresh={refresh}
					setRefreshing={setRefreshing}
					viewMode={viewMode}
					selectedStaff={selectedStaff}
					showBy="fieldStaff"
					serviceRequestId={selectedServiceRequestId}
					visibleColumnIds={visibleColumnIds}
				/>
			)}

			{viewMode === "List" && (
				<TasksList
					fromDate={fromDateList}
					toDate={toDateList}
					setRefresh={setRefresh}
					refresh={refresh}
					setRefreshing={setRefreshing}
					serviceRequestId={selectedServiceRequestId}
					visibleColumnIds={visibleListColumnIds}
				/>
			)}
			<TasksForm />
			<TaskBulkUpload
				show={showBulkUpload}
				onClose={() => setShowBulkUpload(false)}
			/>
		</>
	);
};

export default Tasks;
